/**
 * Permission Form Modal
 * Phase 5: RBAC & Permission System - M3
 * 
 * Create/Edit permission form modal
 * Features: Conditional fields, validation, parent selection
 */

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  MenuItem,
  Alert
} from '@mui/material';
import { createPermission, updatePermission, fetchPermissions } from '../../redux/slices/permissionSlice';
import permissionService from '../../services/permissionService';

const PermissionFormModal = ({ open, onClose, permission }) => {
  const dispatch = useDispatch();
  
  // Parent options için özel liste (tüm aktif ve isMainMenu: true olanlar)
  // NOT: Redux'taki filtrelenmiş permissions state'i yerine özel bir liste kullanıyoruz
  // Çünkü parent dropdown her zaman tüm aktif ve isMainMenu: true olanları göstermeli
  const [parentOptionsList, setParentOptionsList] = useState([]);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    isPage: false,
    path: '',
    isMainMenu: false,
    parentId: '',
    icon: '',
    order: 0,
    isActive: true
  });
  
  const [errors, setErrors] = useState({});
  
  // Fetch all parent options when modal opens (tüm aktif ve isMainMenu: true olanlar)
  useEffect(() => {
    if (open) {
      const fetchParentOptions = async () => {
        try {
          // Parent dropdown için: Tüm aktif ve isMainMenu: true olan permission'ları çek
          const response = await permissionService.getPermissions({
            isMainMenu: true,
            isActive: true
          });
          setParentOptionsList(response || []);
        } catch (error) {
          console.error('Failed to fetch parent options:', error);
          setParentOptionsList([]);
        }
      };
      fetchParentOptions();
    }
  }, [open]);
  
  // Initialize form data when permission or modal open state changes
  useEffect(() => {
    if (permission) {
      setFormData({
        code: permission.code || '',
        name: permission.name || '',
        description: permission.description || '',
        isPage: permission.isPage || false,
        path: permission.path || '',
        isMainMenu: permission.isMainMenu || false,
        parentId: permission.parentId?._id || permission.parentId || '',
        icon: permission.icon || '',
        order: permission.order || 0,
        isActive: permission.isActive !== undefined ? permission.isActive : true
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        isPage: false,
        path: '',
        isMainMenu: false,
        parentId: '',
        icon: '',
        order: 0,
        isActive: true
      });
    }
    setErrors({});
  }, [permission, open]);
  
  const validate = () => {
    const newErrors = {};
    
    // Code validation
    if (!formData.code) {
      newErrors.code = 'Kod gereklidir';
    } else if (!/^[a-z]+\.[a-z]+(\.[a-z]+)?$/.test(formData.code)) {
      newErrors.code = 'Kod formatı: <kaynak>.<aksiyon> (örn: users.view, company.users.manage)';
    }
    
    // Name validation
    if (!formData.name) {
      newErrors.name = 'İsim gereklidir';
    }
    
    // Path validation
    // Path is NOT required even if isPage: true (can default to /dashboard)
    // Path is NOT needed if isPage: false (category or action)
    
    // ParentId validation
    // If isMainMenu: false and isPage: true → Must have parentId OR it's a non-menu page
    // No strict validation needed as all combinations are valid
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validate()) return;
    
    // Clean up data before submission
    const submitData = {
      ...formData,
      // Convert empty strings to null
      path: formData.path || null,
      parentId: formData.parentId || null,
      icon: formData.icon || null,
      description: formData.description || null
    };
    
    if (permission) {
      await dispatch(updatePermission({
        permissionId: permission._id,
        updates: submitData
      }));
    } else {
      await dispatch(createPermission(submitData));
    }
    
    // Refresh parent options list (yeni eklenen veya güncellenen permission parent olabilir)
    try {
      const response = await permissionService.getPermissions({
        isMainMenu: true,
        isActive: true
      });
      setParentOptionsList(response || []);
    } catch (error) {
      console.error('Failed to refresh parent options:', error);
    }
    
    // Refresh permissions list in Redux (filtersız, tüm aktifleri çek)
    dispatch(fetchPermissions({ isActive: true }));
    
    onClose();
  };
  
  // Get parent menu options
  // ⚠️ CRITICAL RULE: Parent seçimi için filtreleme
  // 
  // Parent olabilir:
  // 1. Kategoriler (isMainMenu: true, isPage: false) → Accordion/Group headers
  // 2. Root sayfalar (isMainMenu: true, isPage: true, parentId: null) → Ana menü sayfaları
  // 3. Nested sayfalar (isMainMenu: true, isPage: true, parentId: dolu) → Alt menü sayfaları (derin hiyerarşi için)
  // 
  // KURAL: Sadece isMainMenu: true olanlar parent olabilir
  // (Çünkü sadece menüde görünen öğelerin altına başka öğeler eklenebilir)
  // 
  // İSTİSNA: Kendisi parent olamaz (döngüsel referansı önlemek için)
  // 
  // NOT: parentOptionsList kullanıyoruz (filtrelenmiş permissions state'i yerine)
  // Çünkü parent dropdown tüm aktif ve isMainMenu: true olanları göstermeli
  const parentOptions = parentOptionsList.filter(
    p => p._id !== permission?._id
  );
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {permission ? '✏️ Yetki Düzenle' : '➕ Yeni Yetki Ekle'}
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Info Alert */}
          <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
            <strong>🎯 Menu Logic:</strong><br />
            <strong>• isMainMenu: true, isPage: true</strong> → Ana menüde görünür (PATH var, tıklanabilir)<br />
            <strong>• isMainMenu: true, isPage: true, parentId: dolu</strong> → Parent altında görünür<br />
            <strong>• isMainMenu: true, isPage: false</strong> → Kategori/Accordion (PATH yok, gruplayıcı)<br />
            <strong>• isMainMenu: false, isPage: true</strong> → Sayfa ama menüde yok (buton ile açılır)<br />
            <strong>• isMainMenu: false, isPage: false</strong> → Aksiyon yetkisi (button visible kontrolü)<br />
            <strong>• Path boş ise</strong> → /dashboard'a gider
          </Alert>
          
          {/* Code */}
          <TextField
            label="Kod *"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase() })}
            error={!!errors.code}
            helperText={errors.code || 'Örnek: users.view, company.users.manage, documents.create'}
            disabled={!!permission} // Disable editing code for existing permissions
            fullWidth
          />
          
          {/* Name */}
          <TextField
            label="İsim *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name || 'Türkçe açıklayıcı isim'}
            fullWidth
          />
          
          {/* Description */}
          <TextField
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            helperText="Bu yetkinin ne işe yaradığını açıklayın"
            fullWidth
          />
          
          {/* isPage */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isPage}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  isPage: e.target.checked,
                  // Clear path when unchecked (categories and actions don't have paths)
                  path: e.target.checked ? formData.path : ''
                })}
              />
            }
            label="Sayfa mı? (PATH var, route ile erişilebilir)"
          />
          
          {/* Path (conditional - only for pages) */}
          {formData.isPage && (
            <TextField
              label="Yol (Opsiyonel)"
              value={formData.path}
              onChange={(e) => setFormData({ ...formData, path: e.target.value })}
              error={!!errors.path}
              helperText={errors.path || 'Örnek: /admin/permissions, /users (Boş ise /dashboard)'}
              fullWidth
            />
          )}
          
          {/* isMainMenu (always visible) */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isMainMenu}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  isMainMenu: e.target.checked
                })}
              />
            }
            label="Ana Menüde Görünsün mü?"
          />
          
          {/* ParentId (conditional - only if isMainMenu is true) */}
          {formData.isMainMenu && (
            <TextField
              select
              label="Üst Menü (Opsiyonel)"
              value={formData.parentId}
              onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
              error={!!errors.parentId}
              helperText={errors.parentId || 'Seçilirse bu öğe, parent altında görünür (nested menu)'}
              fullWidth
            >
              <MenuItem value="">Yok (Root seviye)</MenuItem>
              {parentOptions.map(p => {
                // Parent tipini belirle (görselleştirme için)
                let typeLabel = '';
                if (!p.isPage && p.isMainMenu) {
                  typeLabel = '📁 Kategori';
                } else if (p.isPage && p.isMainMenu && !p.parentId) {
                  typeLabel = '📄 Root Sayfa';
                } else if (p.isPage && p.isMainMenu && p.parentId) {
                  typeLabel = '📄 Alt Sayfa';
                }
                
                return (
                  <MenuItem key={p._id} value={p._id}>
                    {p.name} ({p.code}) {typeLabel && ` - ${typeLabel}`}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
          
          {/* Icon (always visible if displayed in menu) */}
          {formData.isMainMenu && (
            <TextField
              label="İkon"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              helperText="MUI icon adı (örn: PeopleIcon, DashboardIcon, SettingsIcon)"
              fullWidth
            />
          )}
          
          {/* Order */}
          <TextField
            type="number"
            label="Sıra"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            helperText="Menüde/listede görünme sırası (küçük numara önce gelir)"
            fullWidth
          />
          
          {/* isActive */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Aktif (Pasif yetkiler soft delete gibi, listede default gelmez)"
          />
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
        >
          {permission ? 'Güncelle' : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PermissionFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  permission: PropTypes.object
};

export default PermissionFormModal;

